/**
 * hashing — Cross-platform cryptographic hash helpers.
 *
 * `sha256Hex` returns the hex-encoded SHA-256 digest of a UTF-8 string
 * or byte buffer. It uses the Web Crypto SubtleCrypto API when
 * available (browsers, modern Node ≥ 18 globals) and falls back to
 * `node:crypto` under non-Web runtimes so the same call works in tests
 * (vitest/jsdom), demo apps, and tooling scripts.
 *
 * Demos use SHA-256 only for *artifact identity*, not for password
 * hashing or message authentication. SHA-3 and BLAKE3 require an
 * external implementation; callers may pass a custom `Hasher` to
 * `hashBytesHex` to plug those in without changing call sites.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

export type HashAlgorithm = "SHA-256" | "SHA-384" | "SHA-512";

export interface Hasher {
  readonly digest: (input: Uint8Array) => Promise<Uint8Array>;
}

function bytesToHex(bytes: Uint8Array): string {
  let hex = "";
  for (const byte of bytes) {
    hex += byte.toString(16).padStart(2, "0");
  }
  return hex;
}

function toBytes(input: string | Uint8Array): Uint8Array {
  if (typeof input === "string") return new TextEncoder().encode(input);
  return input;
}

// `globalThis.crypto` is unavailable in Node runtimes older than 18 and
// in some sandboxed test environments. Read both `crypto` and `subtle`
// behind a runtime guard so module evaluation never throws — the
// node:crypto fallback takes over when the Web Crypto path is missing.
const webCryptoSubtle: SubtleCrypto | undefined = ((): SubtleCrypto | undefined => {
  const maybeCrypto =
    typeof globalThis === "object"
      ? (globalThis as { crypto?: { subtle?: SubtleCrypto } }).crypto
      : undefined;
  return maybeCrypto?.subtle;
})();

async function digestWithWebCrypto(
  algorithm: HashAlgorithm,
  bytes: Uint8Array,
): Promise<Uint8Array> {
  if (webCryptoSubtle === undefined) {
    throw new Error("SubtleCrypto is not available in this runtime");
  }
  const arrayBuffer = await webCryptoSubtle.digest(algorithm, bytes as BufferSource);
  return new Uint8Array(arrayBuffer);
}

interface NodeHashLike {
  update(input: Uint8Array): void;
  digest(): Uint8Array;
}

interface NodeCryptoLike {
  createHash(name: string): NodeHashLike;
}

async function digestWithNodeCrypto(
  algorithm: HashAlgorithm,
  bytes: Uint8Array,
): Promise<Uint8Array> {
  // Dynamic specifier is opaque to bundlers so browser builds tree-
  // shake this branch out; `unknown` cast keeps the file independent
  // of `@types/node`.
  const specifier = "node:crypto";
  const nodeCrypto = (await import(/* @vite-ignore */ specifier)) as NodeCryptoLike;
  const nodeName = algorithm.toLowerCase().replace("-", "");
  const hash = nodeCrypto.createHash(nodeName);
  hash.update(bytes);
  return new Uint8Array(hash.digest());
}

/** Compute the hex-encoded digest of `input` under the given algorithm. */
export async function hashHex(
  input: string | Uint8Array,
  algorithm: HashAlgorithm = "SHA-256",
): Promise<string> {
  const bytes = toBytes(input);
  const digestBytes =
    webCryptoSubtle !== undefined
      ? await digestWithWebCrypto(algorithm, bytes)
      : await digestWithNodeCrypto(algorithm, bytes);
  return bytesToHex(digestBytes);
}

/** Convenience alias for SHA-256, which is the demo default. */
export const sha256Hex = async (input: string | Uint8Array): Promise<string> =>
  hashHex(input, "SHA-256");

/**
 * Compute the digest under a *caller-supplied* hasher. This is the seam
 * for plugging in SHA-3 / BLAKE3 / etc. without bloating the shared
 * library with optional cryptographic dependencies.
 */
export async function hashBytesHex(bytes: Uint8Array, hasher: Hasher): Promise<string> {
  return bytesToHex(await hasher.digest(bytes));
}
